import { useTranslation } from "react-i18next";
import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { GraduationCap, Presentation } from "lucide-react";
import { AppHeader } from "@/components/AppHeader";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { useEducation } from "@/hooks/useEducation";
import { trackRokuCompleteRegistration } from "@/lib/roku";
import { toast } from "sonner";

import { T } from "@/components/T";
export const Route = createFileRoute("/onboarding")({
  component: OnboardingPage,
  head: () => ({
    meta: [
      { title: "Get started - Inquira" },
      { name: "description", content: "Tell Inquira whether you are a student or a teacher so your dashboard fits how you study or teach." },
      { property: "og:title", content: "Get started with Inquira" },
      { property: "og:description", content: "Choose the student or teacher experience in Inquira." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
});

function OnboardingPage() {
  const { t } = useTranslation();
  const { user, isPaid, isLoading, signOut } = useAuth();
  const { chooseRole, teacherPortalEnabled } = useEducation();
  const navigate = useNavigate();
  const [saving, setSaving] = useState<string | null>(null);

  useEffect(() => {
    if (!isLoading && !user) navigate({ to: "/login" });
  }, [isLoading, user, navigate]);

  async function pick(role: "student" | "teacher") {
    setSaving(role);
    const error = await chooseRole(role);
    setSaving(null);
    if (error) { toast.error(error); return; }
    // Roku: registration flow completed (role saved server-side).
    trackRokuCompleteRegistration();
    navigate({ to: role === "teacher" ? "/teacher" : "/classes" });
  }

  return (
    <div className="min-h-screen bg-background">
      <AppHeader user={user} isPaid={isPaid} onSignOut={signOut} />
      <main className="mx-auto max-w-2xl px-4 py-10">
        <h1 className="font-display text-3xl font-black tracking-tight text-ink"><T k="edu.onboarding.how_will_you_use_inquira" /></h1>
        <p className="mt-2 text-sm text-ink-muted">
          You can keep using everything you already have — this just decides which dashboard opens first.
        </p>
        <div className="mt-8 grid gap-4 sm:grid-cols-2">
          <button
            onClick={() => pick("student")}
            disabled={saving !== null}
            className="rounded-2xl border border-rule bg-card p-6 text-left transition-colors hover:border-neon-pink disabled:opacity-60"
          >
            <GraduationCap className="size-7 text-neon-pink" />
            <p className="mt-3 font-display text-xl font-bold text-ink"><T k="edu.onboarding.i_m_a_student" /></p>
            <p className="mt-1 text-sm text-ink-muted">
              Join classes with a code, see assignments and deadlines, and keep your personal AI tutor.
            </p>
          </button>
          <button
            onClick={() => pick("teacher")}
            disabled={saving !== null || !teacherPortalEnabled}
            className="rounded-2xl border border-rule bg-card p-6 text-left transition-colors hover:border-neon-pink disabled:opacity-60"
          >
            <Presentation className="size-7 text-neon-pink" />
            <p className="mt-3 font-display text-xl font-bold text-ink"><T k="edu.onboarding.i_m_a_teacher" /></p>
            <p className="mt-1 text-sm text-ink-muted">
              Create classrooms, invite students, assign work, and grade submissions.
            </p>
            {!teacherPortalEnabled && (
              <p className="mt-2 text-xs font-semibold text-ink-muted"><T k="edu.onboarding.coming_soon_currently_in_testing" /></p>
            )}
          </button>
        </div>
        <p className="mt-6 text-xs text-ink-muted">
          School and platform administrator access is granted by an Inquira administrator, not chosen here.
        </p>
        <Button variant="ghost" className="mt-4" onClick={() => navigate({ to: "/" })}>
          Skip for now
        </Button>
        <div className="mt-8 flex flex-wrap gap-3 border-t border-rule pt-5">
          <Button variant="scan" onClick={() => navigate({ to: "/" })}>{t("monetization.continue_with_free")}</Button>
          <Link to="/pricing"><Button variant="subtle">{t("monetization.compare_premium_plans")}</Button></Link>
        </div>
      </main>
    </div>
  );
}
